import { useCallback, useEffect, useState } from 'react'
import { Button, Col, Container, Row } from 'react-bootstrap'
import Accordion from 'react-bootstrap/Accordion'
import KLayerNavigation from '../components/KLayerNavigation'
import RenderObject from '../components/KRenderKyc'
import AmlChecksPanel from '../components/AmlChecksPanel'
import { isUserLoggedIn } from '../helpers'
import { t } from '../locale'
import { Client, KycCheck } from '../react-app-env'
import { fetchClientById, fetchKycChecks, updateKycStatus } from '../service/client'
import { getServiceKeys } from '../helpers/services.helper'

export default function StammdatenView() {
  const pathSegments = window.location.pathname.split('/')
  const clientId = pathSegments[3]

  const [state, setState] = useState({
    client: {} as Client,
    kycCheck: [] as any[],
    currentKyc: {} as KycCheck,
  })

  const updateState = useCallback(
    (newState: Partial<typeof state>) => setState(prevState => ({ ...prevState, ...newState })),
    []
  )

  const loadClient = useCallback(async () => {
    const result = await fetchClientById(clientId)
    if (result.ok) updateState({ client: result.client })
  }, [clientId, updateState])

  const kycChecks = useCallback(async () => {
    try {
      const response = await fetchKycChecks(clientId)
      response.ok && updateState({ kycCheck: response.result.flatMap((k: any) => k.items || []) })
    } catch (error) {
      console.error('Error fetching KYC checks:', error)
    }
  }, [clientId, updateState])

  useEffect(() => {
    if (!clientId) return

    loadClient()
    kycChecks()
  }, [clientId, loadClient, kycChecks])

  const kycCheckDelete = async (kycNummer: number, kycStatus: string) => {
    const response = await updateKycStatus(clientId, kycNummer, kycStatus)
    response && kycChecks()
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4'>
          <Row className='g-2'>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('clientNumber')}</p>
              <p>{state.client.clientNumber || '—'}</p>
            </Col>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('email')}</p>
              <p>{state.client.email || '—'}</p>
            </Col>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('isPerson')}</p>
              <p>{state.client.isPerson ? t('yes') : t('no')}</p>
            </Col>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('gender')}</p>
              <p>{t(state.client.gender) || '—'}</p>
            </Col>
          </Row>

          <Row className='g-2'>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('firstName')}</p>
              <p>{state.client.firstName || '—'}</p>
            </Col>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('lastName')}</p>
              <p>{state.client.name || '—'}</p>
            </Col>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('dateOfBirth')}</p>
              <p>{state.client.dateOfBirth || '—'}</p>
            </Col>
            <Col xs={12} md={3}>
              <p className='text-primary smaller mb-1'>{t('country')}</p>
              <p>{state.client.country || '—'}</p>
            </Col>
          </Row>

          <Row className='g-2'>
            <Col xs={12}>
              <hr className='my-4' />
            </Col>
          </Row>

          {getServiceKeys().includes('kycCheck') && (
            <div>
              <Accordion className='mb-4'>
                <Accordion.Item eventKey='0'>
                  <Accordion.Header>{t('kycChecksSaved')}</Accordion.Header>
                  <Accordion.Body>
                    {state.kycCheck.some(item => item.Status === 'OK') ? (
                      state.kycCheck
                        .filter(item => item.Status === 'OK')
                        .map((item, index) => (
                          <div
                            key={index}
                            style={{
                              border: '1px solid #ccc',
                              marginBottom: '10px',
                              padding: '10px',
                            }}
                          >
                            <p>
                              <strong>Liste:</strong> {item.Liste}
                            </p>
                            {item.Eintrag && (
                              <div>
                                <RenderObject data={item.Eintrag} />
                                <Button
                                  variant='danger'
                                  onClick={() => kycCheckDelete(item.Eintrag.Nummer, 'deleted')}
                                >
                                  {t('delete')}
                                </Button>
                              </div>
                            )}
                          </div>
                        ))
                    ) : (
                      <p>{t('noResults')}</p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey='1'>
                  <Accordion.Header>{t('kycChecksDeleted')}</Accordion.Header>
                  <Accordion.Body>
                    {state.kycCheck.some(item => item.Status === 'deleted') ? (
                      state.kycCheck
                        .filter(item => item.Status === 'deleted')
                        .map((item, index) => (
                          <div
                            key={index}
                            style={{
                              border: '1px solid #ccc',
                              marginBottom: '10px',
                              padding: '10px',
                            }}
                          >
                            <p>Liste: {item.Liste}</p>
                            {item.Eintrag && (
                              <div>
                                <RenderObject data={item.Eintrag} />

                                <Button
                                  variant='success'
                                  onClick={() => {
                                    updateState({ currentKyc: item })
                                    kycCheckDelete(item.Eintrag.Nummer, 'OK')
                                  }}
                                >
                                  {t('update')}
                                </Button>
                              </div>
                            )}
                          </div>
                        ))
                    ) : (
                      <p>{t('noResults')}</p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          )}

          <AmlChecksPanel clientId={clientId} />
        </main>
      </Row>
    </Container>
  )
}
